import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogContent, DialogTitle, Grid, IconButton, CircularProgress,
    Box, DialogActions, Button, Checkbox, FormControlLabel, Typography, Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CustomAutocomplete from '../../../AutoComplete/AutoComplete';
import axiosInstance from '../../../../lib/axios';
import { toast, ToastContainer } from 'react-toastify';

const FoodAlloc = ({ open, handleClose }) => {
    const [mealType, setMealType] = useState([]);
    const [mealData, setMealData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedMealTypes, setSelectedMealTypes] = useState([]);
    const [alreadyAllocated, setAlreadyAllocated] = useState(new Set());
    const [showAll, setShowAll] = useState(false);

    const [formData, setFormData] = useState({
        MealTypeId: '',
    });

    const userId = sessionStorage.getItem('UserId') || '0';

    useEffect(() => {
        fetchMealTypesForDropdown();
    }, []);

    useEffect(() => {
        if (formData.MealTypeId) {
            fetchFoodData(formData.MealTypeId);
            setShowAll(false);
            setSelectedMealTypes([]);
        } else {
            setMealData([]);
            setSelectedMealTypes([]);
            setAlreadyAllocated(new Set());
            setShowAll(false);
        }
    }, [formData.MealTypeId]);

    const fetchMealTypesForDropdown = async () => {
        try {
            const response = await axiosInstance.post('MstFood/GetdrpMstMealType', {
                Flag: '',
                MealTypeId: 1,
            });
            if (response.data.Status === 0) {
                const transformed = response.data.Data.map((item) => ({
                    Id: item.MealTypeId,
                    Name: item.MealTypeName,
                }));
                setMealType(transformed);
            } else {
                setMealType([]);
            }
        } catch (error) {
            toast.error('Error loading meal types');
            setMealType([]);
        }
    };

    const fetchFoodData = async (mealTypeId) => {
        setLoading(true);
        try {
            const response = await axiosInstance.post('MstFood/RetriveMstMealType', {
                Flag: 'Mealtypealloc',
                MealTypeId: mealTypeId,
            });

            if (response.data.Status === 0) {
                const data = response.data.Data || [];
                setMealData(data);

                const allocatedIds = data
                    .filter((item) => item.MealTypeAllocId && Number(item.MealTypeAllocId) > 0)
                    .map((item) => item.FoodId);

                setAlreadyAllocated(new Set(allocatedIds));
            } else {
                setMealData([]);
                setAlreadyAllocated(new Set());
            }
        } catch (error) {
            toast.error('Error fetching allocation data');
            setMealData([]);
            setAlreadyAllocated(new Set());
        } finally {
            setLoading(false);
        }
    };

    const handleCheckboxChange = (foodId) => (event) => {
        if (event.target.checked) {
            setSelectedMealTypes((prev) =>
                prev.some((item) => item.FoodId === foodId)
                    ? prev
                    : [...prev, { MealTypeId: formData.MealTypeId, FoodId: foodId }]
            );
        } else {
            setSelectedMealTypes((prev) => prev.filter((item) => item.FoodId !== foodId));
        }
    };

    const isChecked = (foodId) => {
        return (
            alreadyAllocated.has(foodId) ||
            selectedMealTypes.some((item) => item.FoodId === foodId)
        );
    };

    const displayedMeals = (() => {
        let list = showAll ? [...mealData] : mealData.filter((meal) => alreadyAllocated.has(meal.FoodId));

        return list.sort((a, b) => {
            const aChecked = isChecked(a.FoodId);
            const bChecked = isChecked(b.FoodId);

            if (aChecked && !bChecked) return -1;
            if (!aChecked && bChecked) return 1;
            return 0;
        });
    })();

    const handleDisplayAll = () => {
        setShowAll(true);
    };

    const handleSubmit = async () => {
        if (selectedMealTypes.length === 0) {
            toast.warn('No changes to save');
            return;
        }

        const payload = selectedMealTypes.map((item) => ({
            MealTypeId: item.MealTypeId,
            FoodId: item.FoodId,
            SeqNo: 1,
            CreatedBy: parseInt(userId, 10) || 1,
        }));

        try {
            const response = await axiosInstance.post('MstFood/MangeMealTypeAlloc', payload);
            if (response.data.Status === 0) {
                toast.success(response.data.Message || 'Allocation saved successfully');
                // handleClose();
                setSelectedMealTypes([]);
                await fetchFoodData(formData.MealTypeId);
            } else {
                toast.error(response.data.Message || 'Failed to save allocation');
            }
        } catch (error) {
            toast.error('Error submitting allocation');
        }
    };

    return (
        <Box>
            <ToastContainer />
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Food Allocation
                    <IconButton onClick={handleClose}>
                        <CloseIcon color="error" />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ maxHeight: 400, overflowY: 'auto' }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <CustomAutocomplete
                                options={mealType}
                                id="MealTypeId-autocomplete"
                                name="MealTypeId"
                                label="Meal Type"
                                value={mealType.find((itm) => itm.Id === formData.MealTypeId) || null}
                                onChange={(e) => {
                                    const selectedId = e.target.value;
                                    setFormData({ ...formData, MealTypeId: selectedId || '' });
                                }}
                                className="custom-textfield"
                                variant="filled"
                            />
                        </Grid>

                        {formData.MealTypeId && (
                            <>
                                {loading ? (
                                    <Grid container justifyContent="center" sx={{ mt: 4 }}>
                                        <CircularProgress />
                                    </Grid>
                                ) : mealData.length === 0 ? (
                                    <Grid item xs={12} sx={{ mt: 3 }}>
                                        <Typography color="text.secondary" align="center">
                                            No foods found
                                        </Typography>
                                    </Grid>
                                ) : displayedMeals.length === 0 && !showAll ? (
                                    <Grid item xs={12} sx={{ mt: 3 }}>
                                        <Typography color="text.secondary" align="center">
                                            No allocated foods yet for this meal type
                                        </Typography>
                                    </Grid>
                                ) : (
                                    <Grid item xs={12} sx={{ mt: 2 }}>
                                        <Grid container spacing={1}>
                                            {displayedMeals.map((meal) => (
                                                <Grid item xs={6} key={meal.FoodId}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                size="small"
                                                                checked={isChecked(meal.FoodId)}
                                                                onChange={handleCheckboxChange(meal.FoodId)}
                                                                sx={{
                                                                    padding: 0, margin: 0, '&.Mui-checked': {
                                                                        color: alreadyAllocated.has(meal.FoodId) ? 'success.main' : 'primary.main',
                                                                        '&.Mui-checked': {
                                                                            color: alreadyAllocated.has(meal.FoodId) ? 'success.main' : 'primary.main',
                                                                        },
                                                                    },
                                                                }}
                                                            />
                                                        }
                                                        label={meal.FoodName || '—'}
                                                        sx={{
                                                            display: 'block',
                                                            m: 0,
                                                            p: 0,
                                                            justifyContent: 'flex-start',
                                                            '& .MuiFormControlLabel-label': { ml: 1 },
                                                        }}
                                                    />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Grid>
                                )}
                            </>
                        )}
                    </Grid>
                </DialogContent>

                <DialogActions>
                    <Button variant="outlined" size="small" color="secondary" onClick={handleDisplayAll}>
                        Display All
                    </Button>
                    <Button variant="contained" size="small" color="error" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button variant="contained" size="small" color="success" onClick={handleSubmit}
                        disabled={selectedMealTypes.length === 0 || loading}
                    >
                        Submit
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default FoodAlloc;